import { supabase } from '@/integrations/supabase/client';
import { Procurement, Farm } from '@/types/database.types';

export const getCoordinatorProcurements = async (coordinatorId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('procurements')
    .select(`
      *,
      requestor:user_profiles!procurements_requestor_id_fkey(id, first_name, last_name, role, created_at, updated_at, status),
      coordinator:user_profiles!procurements_coordinator_id_fkey(id, first_name, last_name, role, created_at, updated_at, status),
      farmer:farms!procurements_farmer_id_fkey(id, farmer_name, phone_number, region),
      costs:procurement_costs(*),
      finance:procurement_finance(*),
      warehouse:procurement_warehouse(*),
      items:procurement_items(*)
    `)
    .eq('coordinator_id', coordinatorId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching coordinator procurements:', error);
    throw new Error(error.message);
  }

  return data || [];
};

export const getCoordinatorProcurementStats = async (coordinatorId: string) => {
  const { data, error } = await supabase
    .from('procurements')
    .select('id, status, total_amount, created_at')
    .eq('coordinator_id', coordinatorId);

  if (error) {
    console.error('Error fetching coordinator procurement stats:', error);
    throw new Error(error.message);
  }

  const procurements = data || [];
  const totalProcurements = procurements.length;
  const totalValue = procurements.reduce((sum, p) => sum + (p.total_amount || 0), 0);
  const activeProcurements = procurements.filter(p => 
    ['initiated', 'finance_approved', 'warehouse_confirmed'].includes(p.status)
  ).length;
  const completedProcurements = procurements.filter(p => p.status === 'completed').length;

  // Recent procurements (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const recentProcurements = procurements.filter(p => 
    new Date(p.created_at) >= thirtyDaysAgo
  ).length;

  return {
    totalProcurements,
    totalValue,
    activeProcurements,
    completedProcurements,
    recentProcurements
  };
};

export const getCoordinatorFarmerRegistrations = async (coordinatorId: string) => {
  const { data, error } = await supabase
    .from('coordinator_farmer_registrations')
    .select(`
      *,
      farmer:farms!coordinator_farmer_registrations_farmer_id_fkey(id, farmer_name, phone_number, region, product_name)
    `)
    .eq('coordinator_id', coordinatorId)
    .order('registration_date', { ascending: false });

  if (error) {
    console.error('Error fetching coordinator farmer registrations:', error);
    throw new Error(error.message);
  }

  return data || [];
};

export const createFarmerRegistration = async (coordinatorId: string, farmerId: string, notes?: string) => {
  const { data, error } = await supabase
    .from('coordinator_farmer_registrations')
    .insert({
      coordinator_id: coordinatorId,
      farmer_id: farmerId,
      notes
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating farmer registration:', error);
    throw new Error(error.message);
  }

  return data;
};